/**
 * Compact, reasonably stable CSS selector for an element, plus masking helpers.
 * We favour ids and a couple of meaningful classes, falling back to nth-of-type,
 * and cap the depth so selectors stay short.
 */

const MAX_DEPTH = 5;

export function cssSelector(el: Element | null): string {
  if (!el || !(el instanceof Element)) return "";
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < MAX_DEPTH) {
    if (node.id) {
      parts.unshift(`#${cssEscape(node.id)}`);
      break; // an id is unique enough to stop here
    }
    let part = node.tagName.toLowerCase();
    const cls = stableClasses(node);
    if (cls) part += `.${cls}`;
    const idx = nthOfType(node);
    if (idx > 1) part += `:nth-of-type(${idx})`;
    parts.unshift(part);
    node = node.parentElement;
    depth++;
  }
  return parts.join(" > ");
}

function stableClasses(el: Element): string {
  // Skip framework-generated/hashed classes (e.g. css-1a2b3c, sc-xxxx).
  const classes = Array.from(el.classList)
    .filter((c) => c.length <= 30 && !/[0-9]{4,}|^css-|^sc-|hash/i.test(c))
    .slice(0, 2);
  return classes.map(cssEscape).join(".");
}

function nthOfType(el: Element): number {
  let i = 1;
  let sib = el.previousElementSibling;
  while (sib) {
    if (sib.tagName === el.tagName) i++;
    sib = sib.previousElementSibling;
  }
  return i;
}

function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(s);
  return s.replace(/([^a-zA-Z0-9_-])/g, "\\$1");
}

// ── Masking ──────────────────────────────────────────────────────────────────

const SENSITIVE_TYPES = new Set([
  "password",
  "email",
  "tel",
  "credit",
  "cc-number",
]);

const SENSITIVE_NAME_RE =
  /pass|pwd|secret|token|otp|ssn|card|cvv|cvc|iban|account|routing/i;

/**
 * Should this element's text / value be redacted before leaving the page?
 * Passwords and obviously-sensitive fields are always masked, regardless of
 * the project's capture-all default; customers add maskSelectors for more.
 */
export function isMasked(
  el: Element,
  maskSelectors: string[],
  unmaskSelectors: string[],
): boolean {
  // Hard floor: passwords, sensitive input types, and cc/otp autocomplete are
  // ALWAYS masked and cannot be defeated by unmaskSelectors (a customer setting
  // unmaskSelectors:["input"] must never ship raw passwords or card numbers).
  if (el instanceof HTMLInputElement) {
    if (SENSITIVE_TYPES.has(el.type)) return true;
    if (el.autocomplete && /cc-|password|one-time/.test(el.autocomplete))
      return true;
  }

  // Below here the customer may explicitly unmask (opt a field into capture).
  if (matchesAny(el, unmaskSelectors)) return false;

  // Heuristic name-based masking (overridable by the unmask check above).
  if (
    el instanceof HTMLInputElement &&
    SENSITIVE_NAME_RE.test(el.name || el.id || "")
  ) {
    return true;
  }
  // Explicit opt-out hooks the customer can sprinkle on the page.
  if (el.closest("[data-wo-mask], .wo-mask, .ph-no-capture")) return true;
  if (matchesAny(el, maskSelectors)) return true;
  return false;
}

function matchesAny(el: Element, selectors: string[]): boolean {
  for (const sel of selectors) {
    try {
      if (el.matches(sel) || el.closest(sel)) return true;
    } catch {
      /* invalid selector — ignore */
    }
  }
  return false;
}

/** Truncate + collapse whitespace for click target text. */
export function elementText(el: Element, masked: boolean): string | undefined {
  if (masked) return undefined;
  const raw = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return undefined;
  return raw.slice(0, 120);
}
